import type {
  ClientGeoArea,
  ClientGPSLocation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Searchbar, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowBackIcon, CloseIcon } from '@/assets';
import { MAX_SEARCH_LENGTH } from '@/constants/search';
import { useGetNearestGeoArea } from '@/features/api/hooks/shared-app/useGetNearestGeoArea';
import { useSearchAvailableGeoAreas } from '@/features/api/hooks/shared-app/useSearchAvailableGeoAreas';
import { ListItem } from '@/features/list/components/ListItem';
import { HELP_REGION_MISSING_URL } from '@/features/profile-setting/constants/links';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedModal } from '@/shared/components/ExtendedModal';
import { CurrentLocationListItem } from '@/shared/components/location/CurrentLocationListItem';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { FallbackContent } from '@/shared/components/state-view/FallbackContent';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { getCurrentLocation } from '@/shared/utils/location';
import { isEmptyOrNull } from '@/shared/utils/string';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export type Props = {
  visible?: boolean;
  onSelect: (id?: string) => void;
  onDismiss: () => void;
};

export const GeoAreaSearch: FC<Props> = ({ visible, onSelect, onDismiss }) => {
  const theme = useAppTheme();
  const { top } = useSafeAreaInsets();

  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'components.search'
  });

  const [search, setSearch] = useState<string>('');
  const [position, setPosition] = useState<ClientGPSLocation>();

  const handleGetCurrentLocation = useCallback(async () => {
    const position = await getCurrentLocation();
    setPosition(position);
  }, []);

  const { data: geoArea, isFetching: isGeoAreaFetching } =
    useGetNearestGeoArea(position);
  const { data: geoAreaScreens, isFetching: isSearchFetching } =
    useSearchAvailableGeoAreas(search);

  const isLoading = useMemo(
    () => isGeoAreaFetching || isSearchFetching,
    [isGeoAreaFetching, isSearchFetching]
  );

  const handleDismiss = useCallback(() => {
    setSearch('');
    setPosition(undefined);
    onDismiss();
  }, [onDismiss]);

  const handleSelectGeoArea = useCallback(
    (id?: ClientGeoArea['id']) => {
      onSelect(id);
      handleDismiss();
    },
    [onSelect, handleDismiss]
  );

  useEffect(() => {
    if (!isEmptyOrNull(geoArea?.id)) handleSelectGeoArea(geoArea.id);
  }, [geoArea, geoArea?.id, handleSelectGeoArea]);

  return (
    <ExtendedModal
      visible={visible}
      onDismiss={handleDismiss}
      onRequestClose={handleDismiss}
      statusBarTranslucent
      animationType="fade"
    >
      <View
        style={{
          flex: 1,
          paddingTop: top,
          backgroundColor: theme.colors.surfaceContainerLow
        }}
      >
        <Searchbar
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          style={{ backgroundColor: theme.colors.surfaceContainerLow }}
          mode="view"
          placeholder={t('placeholder')}
          value={search}
          maxLength={MAX_SEARCH_LENGTH}
          onChangeText={setSearch}
          loading={isLoading}
          onIconPress={onDismiss}
          icon={({ color, size }) => (
            <ArrowBackIcon fill={color} width={size} height={size} />
          )}
          clearIcon={({ color, size }) =>
            !isEmptyOrNull(search) && (
              <CloseIcon fill={color} width={size} height={size} />
            )
          }
        />
        <CurrentLocationListItem
          onPress={handleGetCurrentLocation}
          title={t('location')}
          description={
            isEmptyOrNull(search)
              ? undefined
              : t('resultsHint.locationDescription')
          }
          numberOfLinesDescription={2}
        />
        <KeyboardAwareScrollView keyboardShouldPersistTaps="handled">
          <StateView
            backgroundColor={theme.colors.surfaceContainerLow}
            contentContainerStyle={{ paddingBottom: 32 }}
            isLoading={isLoading}
            isEmpty={search.length > 2 && !hasItems(geoAreaScreens)}
            keyboardShouldPersistTaps="handled"
            centerIndicators={false}
            state={{
              empty: (
                <FallbackContent
                  subtitle={t('state.empty.subtitle')}
                  footer={
                    <OutlinedButton
                      onPress={() => handleOpenUrl(HELP_REGION_MISSING_URL)}
                      labelStyle={{ fontWeight: '500' }}
                    >
                      {t('state.empty.footer')}
                    </OutlinedButton>
                  }
                />
              )
            }}
          >
            {hasItems(geoAreaScreens) &&
              geoAreaScreens?.map(({ id, name, zip, parents }) => (
                <ListItem
                  key={id}
                  overwriteTitleNode={
                    <View style={{ flexDirection: 'row' }}>
                      <Text
                        ellipsizeMode="tail"
                        numberOfLines={1}
                        variant="bodyLarge"
                        style={{ flexShrink: 1, textAlign: 'left' }}
                      >{`${name} ${isEmptyOrNull(zip) ? '' : '(' + zip?.split(',').join('・')}`}</Text>
                      {!isEmptyOrNull(zip) && (
                        <Text variant="bodyLarge">)</Text>
                      )}
                    </View>
                  }
                  description={parents}
                  numberOfLinesDescription={1}
                  hideRightArrow
                  textOrder="column-reverse"
                  onPress={() => handleSelectGeoArea(id)}
                />
              ))}
          </StateView>
        </KeyboardAwareScrollView>
      </View>
    </ExtendedModal>
  );
};
